// frontend/src/hooks/useSessionTimeout.ts
import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuthStore } from '../stores/authStore';

interface UseSessionTimeoutOptions {
  /** Tempo de inatividade em milissegundos antes de desconectar (padrão: 30 minutos) */
  timeout?: number;
  /** Eventos que resetam o timer de inatividade */
  events?: string[];
  /** Callback executado antes do logout (para mostrar aviso, etc) */
  onTimeout?: () => void;
}

interface UseSessionTimeoutReturn {
  showNotification: boolean;
  hideNotification: () => void;
}

/**
 * Hook para simular limite de sessão baseado em inatividade do usuário
 *
 * @example
 * // No componente principal (App.tsx):
 * const { showNotification, hideNotification } = useSessionTimeout({
 *   timeout: 5 * 60 * 1000, // 5 minutos
 *   onTimeout: () => console.log('Sessão expirada por inatividade')
 * });
 *
 * // Para testar rapidamente (30 segundos):
 * useSessionTimeout({ timeout: 30 * 1000 });
 */
export const useSessionTimeout = (options: UseSessionTimeoutOptions = {}): UseSessionTimeoutReturn => {
  const {
    timeout = 30 * 60 * 1000, // 30 minutos por padrão
    events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'],
    onTimeout
  } = options;

  const { isAuthenticated, logout } = useAuthStore();
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const [showNotification, setShowNotification] = useState(false);

  const hideNotification = useCallback(() => {
    setShowNotification(false);
  }, []);

  const handleLogout = useCallback(() => {
    if (onTimeout) {
      onTimeout();
    }
    setShowNotification(true);
    logout();
  }, [logout, onTimeout]);

  const resetTimer = useCallback(() => {
    // Limpa o timer anterior
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    // Apenas cria um novo timer se o usuário estiver autenticado
    if (isAuthenticated) {
      timeoutIdRef.current = setTimeout(() => {
        handleLogout();
      }, timeout);
    }
  }, [isAuthenticated, timeout, handleLogout]);

  useEffect(() => {
    // Só ativa o timeout se o usuário estiver autenticado
    if (!isAuthenticated) {
      return;
    }

    // Inicia o timer
    resetTimer();

    // Adiciona listeners para resetar o timer em atividade do usuário
    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    // Cleanup: remove listeners e limpa o timer
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [isAuthenticated, resetTimer, events]);

  return { showNotification, hideNotification };
};
